import { useCallback, useMemo, useState } from "react";
import { FIRST_PAGE_NUMBER, type IPageRequest } from "@ticketon/rewards-core";
import { useDebounce } from "../util/useDebounce";
import { ALL_OPTION_VALUE } from "./OptionSelect";

const SEARCH_DEBOUNCE_MS = 300;

export type FilterValues = Record<string, string>;

export interface IUsePagedFiltersOptions<TFilters extends FilterValues> {
  /** Text filters whose applied value is debounced (search boxes). */
  debouncedKeys?: readonly (keyof TFilters)[];
  pageSize: number;
}

export interface IUsePagedFilters<TFilters extends FilterValues> {
  /** The values bound to the filter controls. */
  filters: TFilters;
  /** The values to query with (text filters debounced). */
  appliedFilters: TFilters;
  /** Updates one filter and returns to the first page. */
  setFilter: <TKey extends keyof TFilters>(key: TKey, value: TFilters[TKey]) => void;
  /** Restores every filter and returns to the first page. */
  clearFilters: () => void;
  hasActiveFilters: boolean;
  /** 1-based page request for the query. */
  page: IPageRequest;
  setPageNumber: (pageNumber: number) => void;
}

/**
 * Filter + 1-based pagination state shared by the list screens. Changing any
 * filter resets to the first page; search boxes are debounced before querying.
 */
export function usePagedFilters<TFilters extends FilterValues>(
  initialFilters: TFilters,
  { debouncedKeys = [], pageSize }: IUsePagedFiltersOptions<TFilters>,
): IUsePagedFilters<TFilters> {
  const [filters, setFilters] = useState<TFilters>(initialFilters);
  const [pageNumber, setPageNumber] = useState(FIRST_PAGE_NUMBER);
  const debouncedFilters = useDebounce(filters, SEARCH_DEBOUNCE_MS);

  const appliedFilters = useMemo(() => {
    const applied = { ...filters };
    for (const key of debouncedKeys) applied[key] = debouncedFilters[key];
    return applied;
    // debouncedKeys is a static configuration list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, debouncedFilters]);

  const setFilter = useCallback(<TKey extends keyof TFilters>(key: TKey, value: TFilters[TKey]) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
    setPageNumber(FIRST_PAGE_NUMBER);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(initialFilters);
    setPageNumber(FIRST_PAGE_NUMBER);
  }, [initialFilters]);

  const hasActiveFilters = Object.keys(filters).some(
    (key) => filters[key]?.trim() !== initialFilters[key]?.trim(),
  );

  const page = useMemo(() => ({ pageNumber, pageSize }), [pageNumber, pageSize]);

  return { filters, appliedFilters, setFilter, clearFilters, hasActiveFilters, page, setPageNumber };
}

/** API value for a filter control: `undefined` for blank text or the "all" option. */
export function filterValue(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === "" || trimmed === ALL_OPTION_VALUE ? undefined : trimmed;
}

/** API value for a multi-value filter fed by a single select. */
export function filterValues(value: string): string[] | undefined {
  const single = filterValue(value);
  return single === undefined ? undefined : [single];
}
